/**
 * Reusable list tab used by Followers, Following, and Friends tabs in UserProfileModal.
 */

import { UserListItem } from '@components/UserListItem';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import { Users } from 'lucide-react';
import type { ReactElement } from 'react';

interface ListUser {
  id: string;
  displayName: string;
  firstName: string;
  lastName: string;
  isVerified: boolean;
  followerCount: number;
  followingCount: number;
  isFollowedByCurrentUser: boolean;
}

export interface UserListTabProps {
  users: ListUser[];
  loading: boolean;
  hasMore: boolean;
  emptyMessage: string;
  emptySubMessage?: string;
  onLoadMore: () => void;
  onViewProfile: () => void;
}

export function UserListTab({
  users,
  loading,
  hasMore,
  emptyMessage,
  emptySubMessage,
  onLoadMore,
  onViewProfile,
}: UserListTabProps): ReactElement {
  return (
    <div className="p-6 space-y-4">
      {loading && users.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {!loading && users.length === 0 && (
        <div className="text-center py-12 text-theme-text-secondary">
          <Users className="w-8 h-8 mx-auto mb-2 text-theme-text-tertiary" />
          <p>{emptyMessage}</p>
          {emptySubMessage && (
            <p className="text-xs text-theme-text-tertiary mt-1">{emptySubMessage}</p>
          )}
        </div>
      )}

      {users.length > 0 && (
        <>
          {users.map((user) => (
            <UserListItem key={user.id} user={user} onViewProfile={onViewProfile} />
          ))}

          {hasMore && (
            <button
              type="button"
              onClick={onLoadMore}
              className="w-full py-2 text-sm text-theme-text-secondary hover:text-gray-200 transition-colors"
            >
              Load More
            </button>
          )}
        </>
      )}
    </div>
  );
}
